import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin, Truck, Package, Clock, CheckCircle2, AlertTriangle,
  Navigation, Eye, FileText, Camera, User, Map, Radio, Signal,
  ArrowRight, Calendar, Phone, Mail, Download, Share2, RefreshCw
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

const ExecutionTracking = ({ userRole = 'manager' }) => {
  const [activeView, setActiveView] = useState(userRole === 'driver' ? 'tasks' : 'map'); // map, tasks, chain, epod
  const [selectedTask, setSelectedTask] = useState(null);

  // Mock data for active shipments
  const activeShipments = [
    {
      id: 'LOG-2024-003',
      requestId: 'LOG-2024-003',
      vehicle: 'VH-001',
      driver: { id: 'DR-001', name: 'Ahmed Al-Rashid', phone: '+966501234567' },
      from: { name: 'Main Warehouse', location: 'Riyadh', lat: 24.7136, lng: 46.6753 },
      to: { name: 'Distribution Center A', location: 'Jeddah', lat: 21.4858, lng: 39.1925 },
      status: 'in_transit',
      currentLocation: { lat: 23.8859, lng: 45.0792, address: 'Highway 40, Near Buraydah' },
      progress: 45,
      estimatedArrival: '2024-01-28 4:30 PM',
      startedAt: '2024-01-28 8:00 AM',
      items: [
        { sku: 'RAW-234', name: 'Steel Sheets', quantity: 200, weight: 10000 }
      ],
      chainOfCustody: [
        {
          timestamp: '2024-01-28 8:00 AM',
          location: { lat: 24.7136, lng: 46.6753, address: 'Main Warehouse, Riyadh' },
          action: 'pickup',
          person: { name: 'Warehouse Manager', role: 'Picker', signature: 'signed' },
          gpsVerified: true,
          notes: 'Items loaded and verified'
        },
        {
          timestamp: '2024-01-28 8:15 AM',
          location: { lat: 24.7136, lng: 46.6753, address: 'Main Warehouse, Riyadh' },
          action: 'handoff',
          person: { name: 'Ahmed Al-Rashid', role: 'Driver', signature: 'signed' },
          gpsVerified: true,
          notes: 'Driver received shipment, verified count'
        }
      ],
      trackingHistory: [
        { time: '8:00 AM', location: 'Main Warehouse, Riyadh', status: 'Picked Up' },
        { time: '8:15 AM', location: 'Main Warehouse, Riyadh', status: 'Driver Received' },
        { time: '9:30 AM', location: 'Highway 40, Near Qassim', status: 'In Transit' },
        { time: '11:00 AM', location: 'Highway 40, Near Buraydah', status: 'In Transit' }
      ]
    },
    {
      id: 'LOG-2024-007',
      requestId: 'LOG-2024-007',
      vehicle: 'VH-004',
      driver: { id: 'DR-002', name: 'Mohammed Ali', phone: '+966502345678' },
      from: { name: 'Main Warehouse', location: 'Riyadh', lat: 24.7136, lng: 46.6753 },
      to: { name: 'Store #45', location: 'Jeddah', lat: 21.4858, lng: 39.1925 },
      status: 'in_transit',
      currentLocation: { lat: 22.2694, lng: 39.7619, address: 'Highway 40, Near Taif' },
      progress: 75,
      estimatedArrival: '2024-01-28 2:00 PM',
      startedAt: '2024-01-28 6:00 AM',
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS', quantity: 25, weight: 500 },
        { sku: 'OFF-123', name: 'Printer HP', quantity: 10, weight: 200 }
      ],
      chainOfCustody: [
        {
          timestamp: '2024-01-28 6:00 AM',
          location: { lat: 24.7136, lng: 46.6753, address: 'Main Warehouse, Riyadh' },
          action: 'pickup',
          person: { name: 'Warehouse Staff', role: 'Picker', signature: 'signed' },
          gpsVerified: true,
          notes: 'Items scanned and loaded'
        },
        {
          timestamp: '2024-01-28 6:20 AM',
          location: { lat: 24.7136, lng: 46.6753, address: 'Main Warehouse, Riyadh' },
          action: 'handoff',
          person: { name: 'Mohammed Ali', role: 'Driver', signature: 'signed' },
          gpsVerified: true,
          notes: 'Driver verified all items'
        }
      ],
      trackingHistory: [
        { time: '6:00 AM', location: 'Main Warehouse, Riyadh', status: 'Picked Up' },
        { time: '6:20 AM', location: 'Main Warehouse, Riyadh', status: 'Driver Received' },
        { time: '8:00 AM', location: 'Highway 40, Near Qassim', status: 'In Transit' },
        { time: '10:00 AM', location: 'Highway 40, Near Taif', status: 'In Transit' }
      ]
    }
  ];

  // Mock completed deliveries with ePOD
  const completedDeliveries = [
    {
      id: 'LOG-2024-004',
      requestId: 'LOG-2024-004',
      vehicle: 'VH-002',
      driver: { id: 'DR-002', name: 'Mohammed Ali', phone: '+966502345678' },
      from: { name: 'Office Building A', location: 'Riyadh' },
      to: { name: 'Office Building B', location: 'Riyadh' },
      status: 'delivered',
      deliveredAt: '2024-01-27 3:45 PM',
      epod: {
        signature: 'data:image/svg+xml;base64,...', // Mock signature data
        photo: 'https://via.placeholder.com/400x300?text=Delivery+Photo',
        gpsLocation: { lat: 24.7136, lng: 46.6753, address: 'Office Building B, Riyadh' },
        receivedBy: { name: 'Facilities Manager', role: 'Receiver', phone: '+966503456789' },
        condition: 'Good',
        notes: 'All items received in good condition',
        timestamp: '2024-01-27 3:45 PM'
      }
    }
  ];

  const renderLiveMap = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Live Map View - Command Center</h3>
            <p className="text-sm text-gray-600 mt-1">Real-time location of all active vehicles</p>
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Refresh
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Map Visualization */}
        <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ height: '500px' }}>
          {/* Mock map with vehicle markers */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-green-50">
            {activeShipments.map((shipment, index) => (
              <div
                key={shipment.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${30 + index * 20}%`,
                  top: `${40 + index * 15}%`
                }}
              >
                <div className="relative">
                  <div className="w-12 h-12 bg-blue-600 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute top-14 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg p-2 whitespace-nowrap border border-gray-200">
                    <p className="text-xs font-medium text-gray-900">{shipment.id}</p>
                    <p className="text-xs text-gray-600">{shipment.driver.name}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Signal className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-gray-600">Live</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Shipments List */}
        <div className="mt-6 space-y-3">
          {activeShipments.map((shipment) => (
            <div key={shipment.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{shipment.id}</p>
                    <p className="text-sm text-gray-600">
                      {shipment.from.name} → {shipment.to.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {shipment.currentLocation.address}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${shipment.progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{shipment.progress}%</span>
                  </div>
                  <p className="text-xs text-gray-600">ETA: {shipment.estimatedArrival}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTasks = () => (
    <div className="space-y-4">
      {activeShipments.map((shipment) => (
        <motion.div
          key={shipment.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{shipment.id}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {shipment.from.name} → {shipment.to.name}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
            }`}>
              {shipment.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Vehicle & Driver</p>
              <p className="font-medium text-gray-900">{shipment.vehicle}</p>
              <p className="text-sm text-gray-600">{shipment.driver.name}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Progress</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${shipment.progress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900">{shipment.progress}%</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Estimated Arrival</p>
              <p className="font-medium text-gray-900">{shipment.estimatedArrival}</p>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Tracking History</h4>
            <div className="space-y-2">
              {shipment.trackingHistory.map((entry, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-1 bg-blue-100 rounded-full mt-1">
                    <Clock className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{entry.status}</p>
                    <p className="text-xs text-gray-600">{entry.location}</p>
                    <p className="text-xs text-gray-500 mt-1">{entry.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                setActiveView('chain');
                setSelectedTask(shipment.id);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              View Chain of Custody
            </button>
            <button
              onClick={() => alert(`Calling ${shipment.driver.name} at ${shipment.driver.phone}`)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Phone className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderChainOfCustody = () => {
    const shipment = activeShipments.find(s => s.id === selectedTask) || activeShipments[0];
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Chain of Custody</h3>
              <p className="text-sm text-gray-600 mt-1">Digital timestamp and GPS-verified handoff records</p>
            </div>
            <select
              value={selectedTask || ''}
              onChange={(e) => setSelectedTask(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {activeShipments.map(s => (
                <option key={s.id} value={s.id}>{s.id}</option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            {shipment.chainOfCustody.map((entry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="relative pl-8 border-l-2 border-blue-200"
              >
                <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 rounded-full border-2 border-white" />
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{entry.action}</p>
                      <p className="text-sm text-gray-600">{entry.timestamp}</p>
                    </div>
                    {entry.gpsVerified && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        GPS Verified
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Location</p>
                      <p className="text-sm font-medium text-gray-900">{entry.location.address}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {entry.location.lat.toFixed(4)}, {entry.location.lng.toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Person</p>
                      <p className="text-sm font-medium text-gray-900">{entry.person.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{entry.person.role}</p>
                      {entry.person.signature === 'signed' && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Signed
                        </span>
                      )}
                    </div>
                  </div>
                  {entry.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{entry.notes}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderEPOD = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Electronic Proof of Delivery (ePOD)</h3>
        <div className="space-y-4">
          {completedDeliveries.map((delivery) => (
            <motion.div
              key={delivery.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-2 border-green-200 bg-green-50 rounded-lg p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">{delivery.id}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {delivery.from.name} → {delivery.to.name}
                  </p>
                </div>
                <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-medium">
                  DELIVERED
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Delivery Details</h5>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Delivered At</p>
                      <p className="font-medium text-gray-900">{delivery.deliveredAt}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Received By</p>
                      <p className="font-medium text-gray-900">{delivery.epod.receivedBy.name}</p>
                      <p className="text-sm text-gray-600">{delivery.epod.receivedBy.role}</p>
                      <p className="text-xs text-gray-500 mt-1">{delivery.epod.receivedBy.phone}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">GPS Location</p>
                      <p className="font-medium text-gray-900">{delivery.epod.gpsLocation.address}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {delivery.epod.gpsLocation.lat.toFixed(4)}, {delivery.epod.gpsLocation.lng.toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Condition</p>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        {delivery.epod.condition}
                      </span>
                    </div>
                    {delivery.epod.notes && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Notes</p>
                        <p className="text-sm text-gray-900">{delivery.epod.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Proof Documents</h5>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-2">Delivery Photo</p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={delivery.epod.photo}
                          alt="Delivery"
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-2">Digital Signature</p>
                      <div className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="h-20 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Signature Image</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => alert('Downloading ePOD document...')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download ePOD
                </button>
                <button
                  onClick={() => alert('Sharing ePOD...')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Execution & Tracking</h2>
            <p className="text-sm text-gray-600 mt-1">Real-time visibility, chain of custody, and electronic proof of delivery</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            ...(userRole !== 'driver' ? [{ id: 'map', label: 'Live Map', icon: Map }] : []),
            { id: 'tasks', label: userRole === 'driver' ? 'My Tasks' : 'Active Tasks', icon: Package },
            ...(userRole === 'manager' ? [
              { id: 'chain', label: 'Chain of Custody', icon: User },
              { id: 'epod', label: 'ePOD', icon: FileText }
            ] : [])
          ].map((view) => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => {
                  setActiveView(view.id);
                  if (view.id === 'chain' && !selectedTask) {
                    setSelectedTask(activeShipments[0]?.id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeView === view.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {view.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeView === 'map' && renderLiveMap()}
      {activeView === 'tasks' && renderTasks()}
      {activeView === 'chain' && renderChainOfCustody()}
      {activeView === 'epod' && renderEPOD()}
    </div>
  );
};

export default ExecutionTracking;
